import React, { useState, useEffect, useCallback } from 'react'
import { useField } from 'formik'
import FormInputWrapper from './FormInputWrapper'

interface InputAttributes {
  label?: string | null
  className?: string
  placeholder?: string
  disabled?: boolean
  help?: string
  multiple?: boolean
}

type FileData = { file: File; data: string }

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((res, rej) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => res(String(reader.result || ''))
    reader.onerror = (err) => rej(err)
  })

const FormInputFile: React.FC<{ attributes: InputAttributes; name: string }> = (props) => {
  const [field, meta, helpers] = useField(props)
  const { label, className, placeholder, disabled, help, multiple } = props.attributes

  const [loading, setLoading] = useState(false)
  const [imageResults, setImageResults] = useState<string[]>([])
  const [isDragActive, setIsDragActive] = useState(false)

  const { value } = meta
  const { setValue } = helpers

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files)
      const fileDataArray: FileData[] = await Promise.all(
        list.map(async (file) => ({
          file,
          data: await readFileAsDataUrl(file)
        }))
      )

      const imgs = fileDataArray
        .filter((fd) => fd.file.type.startsWith('image/'))
        .map((fd) => fd.data)

      setImageResults(imgs)

      if (multiple) {
        setValue(fileDataArray)
      } else {
        const first = fileDataArray[0]
        setValue(first ?? null)
      }
    },
    [multiple, setValue]
  )

  useEffect(() => {
    if (typeof value === 'string' && value) {
      setImageResults([value])
      setValue(multiple ? [] : null)
    }
  }, [value])

  const uploadedFiles = (() => {
    if (multiple) {
      if (Array.isArray(value) && value.length > 0) {
        if (value.length === 1) {
          const v = value[0] as FileData
          return (
            <>
              <div>{`${v.file.name} – ${(v.file.size / 1_000_000).toFixed(1)} MB`}</div>
            </>
          )
        }
        return (
          <>
            {value.map((file, i) => {
              const v = file as FileData
              return (
                <div key={i}>{`${v.file.name} – ${(v.file.size / 1_000_000).toFixed(1)} MB`}</div>
              )
            })}
          </>
        )
      }
    } else {
      const v = value as FileData | null | undefined
      if (v && (v as any).file) {
        return (
          <>
            <div>{`${v.file.name} – ${(v.file.size / 1_000_000).toFixed(1)} MB`}</div>
          </>
        )
      }
    }
  })()

  const onInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setLoading(true)
    try {
      await handleFiles(files)
    } finally {
      setLoading(false)
    }
  }

  const onDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)

    const files = e.dataTransfer.files
    if (!files || files.length === 0) return

    setLoading(true)
    try {
      await handleFiles(files)
    } finally {
      setLoading(false)
    }
  }

  return (
    <FormInputWrapper className={className} meta={meta}>
      {label !== null && <div className="form-label">{label || <br />}</div>}
      <input
        type="file"
        name={field.name}
        id={`file-input-${field.name}`}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onInputChange}
        onBlur={field.onBlur}
        multiple={!!multiple}
      />
      <label
        htmlFor={`file-input-${field.name}`}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (!isDragActive) {
            setIsDragActive(true)
          }
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={onDrop}
        className={`file-input-label is-title ${isDragActive ? 'is-dragover' : ''}`}
      >
        {loading ? (
          'Nahrávám…'
        ) : (
          <div className="form-file-button border border-[#A8CD00] transition-all duration-300 p-3 rounded-xl">
            <div className="mb-2">
              <span>
                {placeholder || (
                  <>
                    <b>Vyberte</b> nebo přetáhněte soubory
                  </>
                )}
              </span>
              {help && <p className="h-text-size-tiny h-mb-0">{help}</p>}
            </div>
          </div>
        )}
      </label>
      <div>{uploadedFiles}</div>
      {imageResults.length > 0 && (
        <div className="h-mt-2" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {imageResults.map((src, i) => (
            <img key={i} src={src} alt="" style={{ maxHeight: 250, borderRadius: 4 }} />
          ))}
        </div>
      )}
    </FormInputWrapper>
  )
}

export default FormInputFile
